import { readable, writable, get } from 'svelte/store';
import allFacilities from '../public/all_facilities.json';
import centroid from '@turf/centroid';
import { default as lunr } from 'lunr';
import states from './states.json';
import { interpolateNumber, min, max, scaleSequential, color } from 'd3';

const badStates = ['AK', 'HI', 'PR', 'GU', 'VI', 'MP'];

const facilitiesShell = {
  crs: {
    name: 'Prison_Boundaries',
    type: 'FeatureCollection',
    properties: {
      name: 'urn:ogc:def:crs:OGC:1.3:CRS84',
      type: 'name'
    }
  },
  features: []
};

function getURLParams() {
  const u = new URL(window.location);
  const params = [...u.searchParams.entries()];
  return params.reduce((acc, v) => {
    acc[v[0]] = v[1];
    return acc;
  }, {});
}

const urlParams = getURLParams();

allFacilities.features = allFacilities.features.filter((s) => {
  if (urlParams.ice_facilities == 1) {
    return (
      s &&
      badStates.indexOf(s.properties.state) === -1 &&
      s.properties.ice_facility === 'Y'
    );
  }
  return s && badStates.indexOf(s.properties.state) === -1;
});

const allFacilityIds = allFacilities.features.map(
  (f) => f.properties.facilityid
);

// const searchIndex = lunr(function () {
//   this.ref('facilityid');
//   // this.field('state');
//   this.field('fullState');
//   this.field('city');
//   this.field('county');
//   this.field('name');
//   // this.field('zip');

//   allFacilities.features.forEach((f) => {
//     this.add(f.properties);
//   }, this);
// });

// console.log(searchIndex);

function getFactorKey(factor) {
  switch (factor) {
    case 'flood':
      return 'flood_risk_rating';
    case 'heat':
      return 'heat_risk_rating';
    case 'wildfire':
      return 'wildfire_risk_rating';
    default:
      return 'heat_risk_rating';
  }
}

export const searchTerm = writable(null);
export const reboundMap = writable(true);
const floodD = getDomain('flood_risk_rating');
const heatD = getDomain('heat_risk_rating');
const wildfireD = getDomain('wildfire_risk_rating');
export const floodDomain = readable(floodD);
export const heatDomain = readable(heatD);
export const wildfireDomain = readable(wildfireD);
export const activeFacilityIds = writable([]);
export const facilities = createFacilities(allFacilities);
export const centroids = createCentroids();
export const highlightFacilityId = createHighlightFacilityId();
export const factorKey = createFactorKey(
  getFactorKey(urlParams.factor || 'heat')
);
export const facilitySearchToggle = createFacilitySearchToggle('facility');
export const facilitySearchResults = writable([]);
export const listCount = readable(
  urlParams.ice_facilities == 1 ? allFacilities.features.length : 30
);
export const iceOnly = readable(urlParams.ice_facilities == 1);

// scales

const floodColorInterpolator = (alpha = 1, d) => {
  // console.log(d);
  return color(
    `hsla(216, 77%, ${scaleSequential(
      [0, 1],
      interpolateNumber(68, 25)
    )(d)}%, ${alpha})`
  );
};

const heatColorInterpolator = (alpha = 1, d) => {
  // console.log(d);
  return color(
    `hsla(0, 77%, ${scaleSequential(
      [0, 1],
      interpolateNumber(68, 25)
    )(d)}%, ${alpha})`
  );
};

const wildfireColorInterpolator = (alpha = 1, d) => {
  // console.log(d);
  return color(
    `hsla(21, 77%, ${scaleSequential(
      [0, 1],
      interpolateNumber(68, 25)
    )(d)}%, ${alpha})`
  );
};

export const floodColorScale = scaleSequential(
  floodD,
  floodColorInterpolator.bind(null, 1)
);
export const heatColorScale = scaleSequential(
  heatD,
  heatColorInterpolator.bind(null, 1)
);
export const wildfireColorScale = scaleSequential(
  wildfireD,
  wildfireColorInterpolator.bind(null, 1)
);

export const floodColorScaleBG = scaleSequential(
  floodD,
  floodColorInterpolator.bind(null, 0.65)
);
export const heatColorScaleBG = scaleSequential(
  heatD,
  heatColorInterpolator.bind(null, 0.65)
);
export const wildfireColorScaleBG = scaleSequential(
  wildfireD,
  wildfireColorInterpolator.bind(null, 0.65)
);

function getDomain(factorKey) {
  return [
    //min(allFacilities.features, (d) => d.properties[factorKey]),
    0,
    max(allFacilities.features, (d) => d.properties[factorKey])
  ];
}

function isState(val) {
  return !!states.filter(
    (state) =>
      state.abbrev.toLowerCase() === val || state.state.toLowerCase() === val
  )[0];
}

function formatSearchTerm(term) {
  // term.indexOf(' ') > -1 ? `"${term}"` : term;
  if (term.indexOf(' ') === -1) {
    return `${term}${term.length > 1 ? '*' : ''}`;
  }
  const parts = term.split(' ');
  const formatted = parts.reduce((acc, v, i) => {
    if (v === '') {
      return acc;
    }
    if (i === parts.length - 1) {
      return `${acc}${v.length > 1 ? '+' : ''}${v}${v.length > 1 ? '*' : ''}`;
    } else {
      return `${acc}+${v} `;
    }
  }, '');

  return formatted;
}

function findMatches(keys, matcher, searchTerm, obj) {
  const matches = keys.filter((k) => {
    return matcher(searchTerm, obj.properties[k]);
  });

  if (
    matcher(
      searchTerm.replace(/, /gi, ' '),
      `${obj.properties.city} ${obj.properties.state}`
    ) ||
    matcher(
      searchTerm.replace(/, /gi, ' '),
      `${obj.properties.city} ${obj.properties.fullState}`
    ) ||
    matcher(
      searchTerm.replace(/, /gi, ' '),
      `${obj.properties.county} ${obj.properties.fullState}`
    ) ||
    matcher(
      searchTerm.replace(/, /gi, ' '),
      `${obj.properties.county} ${obj.properties.fullState}`
    )
  ) {
    return true;
  }

  return matches.length > 0;
}

function searchResults(searchTerm) {
  const searchFields = ['state', 'fullState', 'city', 'county', 'name', 'zip'];
  const valMatcher = (a, b) => {
    return a.toLowerCase().trim() === b.toLowerCase().trim();
  };

  // console.log(searchTerm, `^${searchTerm}+`);
  const regexMatcher = (a, b) => {
    let re = new RegExp(`${a}+`, 'i');
    return re.test(b);
  };

  const match = findMatches.bind(null, searchFields, regexMatcher, searchTerm);
  return allFacilities.features.reduce(
    (acc, v) => {
      const isMatch = match(v);
      if (isMatch) {
        acc.ids.push(v.properties.facilityid);
        acc.features.push(v);
      }

      return acc;
    },
    { ids: [], features: [] }
  );
}

function createFacilities(facilities) {
  const { subscribe, set, update } = writable(facilities);

  activeFacilityIds.set(allFacilityIds);

  const reset = () => {
    set(allFacilities);
    activeFacilityIds.set(allFacilityIds);
  };

  const search = (searchTerm) =>
    update((c) => {
      highlightFacilityId.set(null);
      searchTerm = { term: searchTerm };
      // if (searchTerm === '' || get(facilitySearchToggle) === 'facility') {
      if (searchTerm.term === '') {
        // console.log('ressssset');
        reset();
      }

      let results;
      // console.log(`${searchTerm}
      // ${formatSearchTerm(searchTerm.term)}`);

      if (isState(searchTerm.term.toLowerCase())) {
        results = allFacilities.features.filter(
          (f) =>
            f.properties.state.toLowerCase() ===
              searchTerm.term.toLowerCase() ||
            f.properties.fullState.toLowerCase() ===
              searchTerm.term.toLowerCase()
        );
        activeFacilityIds.set(results.map((f) => f.properties.facilityid));
        reboundMap.set(true);
        facilitiesShell.features = results;
      } else {
        results = searchResults(searchTerm.term);
        // console.log(results);
        reboundMap.set(true);
        activeFacilityIds.set(results.ids);
        facilitiesShell.features = results.features;
      }
      return facilitiesShell;
    });

  return {
    subscribe,
    search,
    reset
  };
}

function createCentroids() {
  const { subscribe, set, update } = writable(true);

  return {
    subscribe,
    toggle: () => update((c) => !c),
    reset: () => set(false)
  };
}

function createHighlightFacilityId() {
  const { subscribe, set, update } = writable();

  return {
    subscribe,
    set: (v) => {
      if (v) {
        reboundMap.set(true);
      } else {
        reboundMap.set(true);
      }
      set(v);
    },
    update: update((v) => {
      if (v) {
        reboundMap.set(true);
      } else {
        reboundMap.set(true);
      }
      return v;
    })
  };
}

function createFactorKey(initialKey) {
  const { subscribe, set, update } = writable(initialKey);

  let highlightval;

  highlightFacilityId.subscribe((v) => {
    highlightval = v;
  });

  return {
    subscribe,
    set: (v) => {
      if (highlightval) {
        reboundMap.set(false);
      } else {
        reboundMap.set(true);
      }
      set(v);
    },
    update: update((v) => {
      // console.log('update', highlightval);
      if (highlightval) {
        reboundMap.set(false);
      } else {
        reboundMap.set(true);
      }
      return v;
    })
  };
}

function createFacilitySearchToggle(initialValue) {
  const { subscribe, set, update } = writable(initialValue);

  let searchTermVal;

  searchTerm.subscribe((v) => {
    searchTermVal = v;
  });

  return {
    subscribe,
    set: (v) => {
      // console.log('searchtermval', searchTermVal);
      if (v === 'search' && searchTermVal) {
        // console.log('searchtermval', searchTermVal);
        facilities.search(searchTermVal);
      } else {
        // console.log('set', v);
        facilities.reset();
      }
      set(v);
    },
    update: update((v) => {
      if (v === 'search' && searchTermVal) {
        // console.log('searchtermval', searchTermVal);
        facilities.search(searchTermVal);
      } else {
        // console.log('set', v);
        facilities.reset();
      }
      return v;
    })
  };
}

// different facility lists
// all facilities (canonical list of facilities)
// map bounded (only the facilities that fall within the visible map bounds)
// search result bounded (only the facilities that match a users search term)
